import { useCallback, useEffect, useRef } from "react";
import { SelectOptions } from "../../../../internal/components/SelectOptions";
import { OptionsPanelHandle } from "../../../../internal/components/OptionsPanel";
import { SelectFieldInput } from "./SelectFieldInput";
import { SelectFieldLabel } from "./SelectFieldLabel";
import { useDebouncedCallback } from "../../../../internal/hooks/useDebouncedCallback";
import styles from "../../../../components/SelectTrigger/internal/SelectTriggerBase.module.scss";
import { useCombo } from "./useCombo";
import { OptionsPopover } from "../../../../internal/components/OptionsPopover/OptionsPopover";
import { OptionsDialog } from "../../../../internal/components/OptionsDialog/OptionsDialog";
import { FieldMessage } from "../../../../components/FieldMessage";
import { SelectFieldComboboxInternalProps } from "../../../../internal/types/selectFieldInternalTypes";

/**
 * Combobox mode: searchable select using useCombobox.
 * This is the default behavior when disableSearch is false.
 */
export const SelectFieldComboboxMode = ({
  id,
  helperUid,
  selectedOption,
  onSelectedOptionChange,
  displayAs,
  isDisabledOrReadOnly,
  disabled,
  labelAiMark,
  readOnly,
  required,
  placeholder,
  size,
  error,
  hasHelperText,
  disableClearButton,
  prefix,
  suffix,
  label,
  hideLabel,
  hint,
  errorMessages,
  warning,
  description,
  className,
  style,
  layoutStyles,
  pinned,
  groupToString,
  groupSorter,
  virtualize,
  options,
  loading,
  loadingMore,
  hasMore,
  loadOptions,
  loadMore,
  initialLoad,
  initialLoadPerformed,
  setInitialLoadPerformed,
  inputWrapperRef,
  debounceMs,
}: SelectFieldComboboxInternalProps) => {
  const listRef = useRef<OptionsPanelHandle>(null);

  const debouncedOptionLoader = useDebouncedCallback((inputValue: string) => {
    loadOptions(selectedOption?.label === inputValue ? "" : inputValue, {
      initial: true,
    });
  }, debounceMs);

  const adjustInputValueForSelectedOption = useCallback(
    (inputValue: string) => {
      return selectedOption?.label === inputValue ? "" : inputValue;
    },
    [selectedOption?.label],
  );

  const {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getInputProps,
    wrapperDivRef,
    highlightedIndex,
    getItemProps,
    inputValue,
    items,
    closeMenu,
    sectionsMeta,
  } = useCombo({
    displayAs,
    disabled: isDisabledOrReadOnly,
    options,
    pinned,
    groupToString,
    groupSorter,
    selectedOption,
    virtualize,
    onSelectedOptionChange: (opt) => onSelectedOptionChange(opt),
    async onIsOpenChange(changes) {
      if (initialLoad === "open" && !initialLoadPerformed && changes.isOpen) {
        await loadOptions(adjustInputValueForSelectedOption(inputValue), {
          initial: true,
        });
        setInitialLoadPerformed(true);
      }
    },
    onInputValueChange: ({ inputValue: iv }) => {
      debouncedOptionLoader(iv ?? "");
    },
    onScrollToHighlight: (i) => listRef.current?.scrollToIndex(i),
  });

  useEffect(() => {
    const performInitialLoad = async () => {
      if (initialLoad === "immediate" && !initialLoadPerformed) {
        await loadOptions(adjustInputValueForSelectedOption(inputValue), {
          initial: true,
        });
        setInitialLoadPerformed(true);
      }
    };
    performInitialLoad();
  }, [
    loadOptions,
    initialLoadPerformed,
    initialLoad,
    inputValue,
    adjustInputValueForSelectedOption,
    setInitialLoadPerformed,
  ]);

  const handleLoadMore = async () => {
    await loadMore(adjustInputValueForSelectedOption(inputValue));
  };

  if (displayAs === "dialog") {
    return (
      <div
        className={
          className
            ? `${styles["search-field"]} ${className}`
            : styles["search-field"]
        }
        style={{ ...layoutStyles, ...style }}
        ref={wrapperDivRef}
      >
        <SelectFieldLabel
          aiMark={labelAiMark}
          label={label}
          hideLabel={hideLabel}
          getLabelProps={getLabelProps}
          required={required}
        />
        <SelectFieldInput
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          disableToggleButton={false}
          placeholder={placeholder}
          disableClearButton={disableClearButton}
          toggleButtonProps={getToggleButtonProps()}
          onClear={() => onSelectedOptionChange(null)}
          id={`${id}-input`}
          inputProps={getInputProps({
            "aria-describedby": hasHelperText ? helperUid : undefined,
            "aria-invalid": error ? true : undefined,
          })}
          size={size}
          error={!!error}
          prefix={prefix}
          suffix={suffix}
        />
        {hasHelperText ? (
          <FieldMessage
            id={helperUid}
            hint={hint}
            error={errorMessages}
            warning={warning}
            description={description}
          />
        ) : null}
        <OptionsDialog
          id={`${id}-dialog`}
          isOpen={isOpen}
          onClose={closeMenu}
          title={label}
          field={
            <SelectFieldInput
              disabled={disabled}
              readOnly={readOnly}
              inputProps={getInputProps()}
              disableToggleButton
              placeholder={placeholder}
              disableClearButton={disableClearButton}
              onClear={() => onSelectedOptionChange(null)}
              id={`${id}-dialog-input`}
              inputWrapperRef={inputWrapperRef}
              size="medium"
              error={!!error}
              prefix={prefix}
              suffix={suffix}
            />
          }
          initialFocusResolver={(elements) => {
            return (
              elements.find((element) => element.tagName === "INPUT") ||
              elements[0]
            );
          }}
        >
          <SelectOptions
            ref={listRef}
            isOpen
            items={items}
            sectionsMeta={sectionsMeta}
            getMenuProps={getMenuProps}
            getItemProps={getItemProps}
            highlightedIndex={highlightedIndex}
            selectedOption={selectedOption}
            hasMore={hasMore}
            onLoadMore={handleLoadMore}
            loading={loading}
            loadingMore={loadingMore}
            disabled={isDisabledOrReadOnly}
            virtualize={virtualize}
          />
        </OptionsDialog>
      </div>
    );
  }

  return (
    <div
      className={
        className
          ? `${styles["search-field"]} ${className}`
          : styles["search-field"]
      }
      style={{ ...layoutStyles, ...style }}
    >
      <SelectFieldLabel
        aiMark={labelAiMark}
        label={label}
        hideLabel={hideLabel}
        getLabelProps={getLabelProps}
        required={required}
      />
      <SelectFieldInput
        disabled={disabled}
        readOnly={readOnly}
        required={required}
        inputProps={getInputProps({
          popovertarget: `${id}-popover`,
          "aria-describedby": hasHelperText ? helperUid : undefined,
          "aria-invalid": error ? true : undefined,
        })}
        toggleButtonProps={getToggleButtonProps()}
        disableToggleButton={false}
        placeholder={placeholder}
        disableClearButton={disableClearButton}
        onClear={() => onSelectedOptionChange(null)}
        id={`${id}-input`}
        inputWrapperRef={inputWrapperRef}
        size={size}
        error={!!error}
        prefix={prefix}
        suffix={suffix}
      />
      {hasHelperText ? (
        <FieldMessage
          id={helperUid}
          hint={hint}
          error={errorMessages}
          warning={warning}
          description={description}
        />
      ) : null}

      <OptionsPopover
        id={`${id}-popover`}
        referenceElement={inputWrapperRef}
        open={isOpen && displayAs === "popover"}
        onClickOutside={closeMenu}
        width="reference"
      >
        <SelectOptions
          ref={listRef}
          isOpen={isOpen}
          items={items}
          sectionsMeta={sectionsMeta}
          getMenuProps={getMenuProps}
          getItemProps={getItemProps}
          highlightedIndex={highlightedIndex}
          selectedOption={selectedOption}
          hasMore={hasMore}
          onLoadMore={handleLoadMore}
          loading={loading}
          loadingMore={loadingMore}
          disabled={isDisabledOrReadOnly}
          virtualize={virtualize}
        />
      </OptionsPopover>
    </div>
  );
};
